import { arrayOf, string } from 'prop-types';
import React from 'react';
import { Alert } from 'reactstrap';

import ShadeCard from '../../generic/ShadeCard';
import Tabs from '../../generic/Tabs';
import Titled from '../../layout/Titled';
import { benchmarkRecommendationShape, productShape } from '../../prop-types';
import {
  AGGREGATION_LEVEL_COLOR_MAP,
  aggregationLevelDescriptions,
  KPIs,
  KPIsInOrder,
} from '../constants';
import DistributionTable from '../DistributionTable';
import KpiHighlighter from '../KpiHighlighter';
import { getKpiDistribution } from '../utils';

function getTabs(data, products, countries) {
  const { avgKPIs, granularKPIs, countryTotals, productTotals } = data;

  if (!avgKPIs) return [];

  const availableKPIs = Object.keys(avgKPIs);
  const sortedKPIs = KPIsInOrder.filter(kpi => availableKPIs.includes(kpi));

  return sortedKPIs.map(kpi => ({
    label: `${KPIs[kpi]} (${avgKPIs[kpi]})`,
    value: kpi,
    content: (
      <DistributionTable
        kpi={kpi}
        products={products}
        countries={countries}
        countryTotals={countryTotals}
        productTotals={productTotals}
        shares={getKpiDistribution(granularKPIs, kpi)}
        displayComponent={share => {
          if (!share) return '-';

          return (
            <KpiHighlighter
              title={aggregationLevelDescriptions[share.aggregationLevel]}
              value={share.value}
              aggregationLevel={share.aggregationLevel}
            />
          );
        }}
      />
    ),
  }));
}

function TargetKpiBenchmarks({ data, products, countries }) {
  const tabs = getTabs(data, products, countries);

  if (!tabs.length) {
    return (
      <Alert color="danger">
        No benchmarks available for the current selections. Please choose a
        different combination.
      </Alert>
    );
  }

  const shades = AGGREGATION_LEVEL_COLOR_MAP.map((color, index) => ({
    color,
    title: aggregationLevelDescriptions[index],
  }));

  return (
    <Titled
      title="Target KPI Benchmarks"
      actions={[
        <ShadeCard
          startLabel="Most reliable"
          endLabel="Least reliable"
          shades={shades}
        />,
      ]}
    >
      <Tabs
        tabs={tabs}
        enableRouteBasedState={false}
        id="targetKPIBenchmarks"
      />
    </Titled>
  );
}

TargetKpiBenchmarks.propTypes = {
  countries: arrayOf(string.isRequired).isRequired,
  products: arrayOf(productShape.isRequired).isRequired,
  data: benchmarkRecommendationShape.isRequired,
};

export default TargetKpiBenchmarks;
